import { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { categoryColor } from "../lib/colors";
import { projects, type Project } from "../lib/projects";

const filters = [
  "All",
  "Leading now",
  "Industry",
  "University",
  "Personal",
] as const;
type Filter = (typeof filters)[number];

export function ProjectIndex() {
  const [filter, setFilter] = useState<Filter>("All");
  const previewRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  const shown = useMemo(
    () =>
      filter === "All"
        ? projects
        : projects.filter((p) => p.category === filter),
    [filter],
  );

  const move = (event: React.MouseEvent) => {
    const preview = previewRef.current;
    if (!preview) return;
    preview.style.setProperty("--px", `${event.clientX}px`);
    preview.style.setProperty("--py", `${event.clientY}px`);
  };

  const enter = (project: Project) => {
    const preview = previewRef.current;
    const img = imgRef.current;
    if (!preview || !img || !project.image) return;
    img.src = project.image;
    preview.classList.add("is-shown");
  };

  const leave = () => {
    previewRef.current?.classList.remove("is-shown");
  };

  return (
    <section className="index" id="index" onMouseMove={move}>
      <div className="index__head" data-reveal>
        <span className="eyebrow">
          The full index — {projects.length} projects
        </span>
        <h2>
          Everything I&rsquo;ve
          <br />
          <span className="hl hl--violet">had a hand in.</span>
        </h2>
      </div>

      <div className="index__filters" data-reveal>
        {filters.map((option) => (
          <button
            key={option}
            type="button"
            className={option === filter ? "is-active" : ""}
            onClick={() => setFilter(option)}
            style={
              {
                "--chip":
                  option === "All"
                    ? "var(--accent)"
                    : categoryColor[option as Project["category"]],
              } as React.CSSProperties
            }
          >
            {option}
          </button>
        ))}
      </div>

      <ul className="index__list">
        {shown.map((project, order) => (
          <li
            key={project.id}
            data-reveal
            style={{ transitionDelay: `${order * 24}ms` }}
          >
            <Link
              to={`/work/${project.id}`}
              className="index__row"
              onMouseEnter={() => enter(project)}
              onMouseLeave={leave}
              style={
                {
                  "--chip": categoryColor[project.category],
                } as React.CSSProperties
              }
            >
              <span className="index__num">
                {String(order + 1).padStart(2, "0")}
              </span>
              <span className="index__name">{project.name}</span>
              <span className="index__role">{project.role}</span>
              <span className="index__cat">
                <i aria-hidden="true" />
                {project.category}
              </span>
              <span className="index__year">{project.year}</span>
              <span className="index__arrow" aria-hidden="true">
                ↗
              </span>
            </Link>
          </li>
        ))}
      </ul>

      <div className="index__preview" ref={previewRef} aria-hidden="true">
        <img ref={imgRef} alt="" />
      </div>
    </section>
  );
}
